import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Header } from "../components/Header";
import { useOrderDetail } from "../hooks/useOrderDetail";
import { formatDetailDate, formatDetailDateTime, getStatusClass, getInitials } from "../lib/format";

export const Route = createFileRoute("/orders/$invoiceId")({
  component: OrderDetailPage,
});

function scanDelivery(orderMetadata: any, customerPath?: string) {
  if (!orderMetadata) return { url: "", label: "", type: "" };
  const source = orderMetadata.source || "";
  if (source.includes("order_up_pro")) {
    return {
      type: "Club Member",
      label: "Download scans ›",
      url: customerPath
        ? `https://club.atxfilmlab.com/${encodeURIComponent(customerPath.replace(/\/$/, ""))}/${encodeURIComponent(orderMetadata.order_id)}`
        : "",
    };
  }
  if (source.includes("order_up_gallery")) {
    return { type: "Gallery", label: "Download scans ›", url: `https://orders.atxfilmlab.com/orders/${encodeURIComponent(orderMetadata.order_id)}` };
  }
  return { type: "Zip File", label: "Download scans ›", url: `https://downloads.atxfilmlab.com/scans/${encodeURIComponent(orderMetadata.order_id)}.zip` };
}

function OrderDetailPage() {
  const { invoiceId } = Route.useParams();
  const { data, isLoading, error } = useOrderDetail(invoiceId);

  useEffect(() => {
    document.title = "Order " + invoiceId.substring(0, 12) + " - TPFL";
  }, [invoiceId]);

  const notFound = (data && data.error) || (!isLoading && !data);

  return (
    <>
      <Header variant="detail" />
      <div className="detail-breadcrumbs" id="breadcrumbNav">
        <a href="/">Search</a>
        <span className="breadcrumb-sep"><i className="ph ph-caret-right" /></span>
        {data && !data.error ? (
          <>
            <a href={`/users/${encodeURIComponent(data.customerId || "")}`}>{data.customerName || "Customer"}</a>
            <span className="breadcrumb-sep"><i className="ph ph-caret-right" /></span>
            <span className="breadcrumb-current">Order {invoiceId.substring(0, 12)}...</span>
          </>
        ) : (
          <span className="breadcrumb-current">Loading...</span>
        )}
      </div>

      <div className="detail-page-content" id="detailContent">
        {isLoading ? (
          <div className="loading-message">Loading order details...</div>
        ) : error || notFound ? (
          <div className="loading-message">{error ? "Error loading order data" : "Order not found"}</div>
        ) : (
          <OrderBody data={data} invoiceId={invoiceId} />
        )}
      </div>
    </>
  );
}

function OrderBody({ data, invoiceId }: { data: any; invoiceId: string }) {
  const { customerId, customerName, customerEmail, filmDrop, allOrders, status, orderMetadata, customerPath } = data;
  const dropOffDate = filmDrop?.timestamp ? formatDetailDate(filmDrop.timestamp) : allOrders?.["-date"] || "Unknown";
  const sd = scanDelivery(orderMetadata, customerPath);

  const totalRolls = filmDrop
    ? (filmDrop["color-35"] || 0) + (filmDrop["bw-35"] || 0) + (filmDrop["color-120"] || 0) + (filmDrop["bw-120"] || 0)
    : 0;

  const rollTypes: { type: string; qty: number }[] = [];
  if (filmDrop?.["color-35"]) rollTypes.push({ type: "Color 35mm", qty: filmDrop["color-35"] });
  if (filmDrop?.["bw-35"]) rollTypes.push({ type: "B&W 35mm", qty: filmDrop["bw-35"] });
  if (filmDrop?.["color-120"]) rollTypes.push({ type: "Color 120", qty: filmDrop["color-120"] });
  if (filmDrop?.["bw-120"]) rollTypes.push({ type: "B&W 120", qty: filmDrop["bw-120"] });

  // Buy-label URL
  const labelParams = new URLSearchParams();
  if (filmDrop?.["full-name"]) labelParams.set("name", filmDrop["full-name"]);
  if (filmDrop?.email) labelParams.set("email", filmDrop.email);
  if (filmDrop && (filmDrop.street || filmDrop.city || filmDrop.state || filmDrop.zip)) {
    if (filmDrop.street) labelParams.set("street1", filmDrop.street);
    if (filmDrop.street2) labelParams.set("street2", filmDrop.street2);
    if (filmDrop.city) labelParams.set("city", filmDrop.city);
    if (filmDrop.state) labelParams.set("state", filmDrop.state);
    if (filmDrop.zip) labelParams.set("zip", filmDrop.zip);
  } else if (filmDrop?.["mailing-address"]) {
    labelParams.set("address", filmDrop["mailing-address"]);
  }
  const buyLabelUrl = "https://labels.atxfilmlab.com?" + labelParams.toString();
  const showBuyLabelBtn = filmDrop && !filmDrop["mail-back-pdf"];

  const docs: { label: string; url: string; icon: string }[] = [];
  if (filmDrop?.["customer-form"]) docs.push({ label: "Order Form", url: filmDrop["customer-form"], icon: "📄" });
  if (filmDrop?.["mail-back-pdf"]) docs.push({ label: "Return Label", url: filmDrop["mail-back-pdf"], icon: "📦" });
  if (filmDrop?.["mail-in-pdf"]) docs.push({ label: "Mail-in Label", url: filmDrop["mail-in-pdf"], icon: "✉️" });

  return (
    <>
      <div className="order-detail-header">
        <h1>Order Details - Drop off: {dropOffDate}</h1>
        <div className="order-detail-subtitle">Invoice ID: {invoiceId}</div>
        <div className="action-buttons" style={{ marginTop: 16 }}>
          {invoiceId.startsWith("in_") && (
            <a href={`https://dashboard.stripe.com/invoices/${invoiceId}`} target="_blank" rel="noreferrer" className="action-button stripe-action">
              💳 View Invoice in Stripe
            </a>
          )}
          {customerId?.startsWith("cus_") && (
            <a href={`https://dashboard.stripe.com/customers/${customerId}`} target="_blank" rel="noreferrer" className="action-button stripe-action">
              👤 View Customer in Stripe
            </a>
          )}
          {sd.url && (
            <a href={sd.url} target="_blank" rel="noreferrer" className="action-button scan-delivery-action-btn">
              {sd.label}
            </a>
          )}
        </div>
      </div>

      <a href={`/users/${encodeURIComponent(customerId || "")}`} className="customer-link-card">
        <div className="customer-link-info">
          <div className="customer-link-avatar">{getInitials(customerName)}</div>
          <div>
            <div className="customer-link-name">{customerName || "Unknown"}</div>
            <div className="customer-link-email">{customerEmail || "No email"}</div>
          </div>
        </div>
        <span className="customer-link-arrow">→</span>
      </a>

      {allOrders && (
        <div className="detail-card">
          <h2>Order Information</h2>
          <div className="detail-grid">
            <DI label="Completed Date" value={allOrders["-date"] || "N/A"} />
            <DI label="Twin Check" value={allOrders["Twin Check Stickers"] || "N/A"} />
            <DI label="Customer" value={allOrders["full-name"] || customerName || "N/A"} />
            <DI label="Roll Types" value={allOrders["roll-types"] || "N/A"} />
            <DI label="Amount" value={allOrders["amount-of-rolls"] || 0} />
            <DI label="Return Option" value={allOrders["return-option"] || "N/A"} />
            <div className="detail-item">
              <div className="detail-label">Status</div>
              <div className="detail-value"><span className={`status-badge ${getStatusClass(status)}`}>{status}</span></div>
            </div>
            <DI label="Prints" value={allOrders.prints || "None"} />
            {allOrders["pickup-time"] && <DI label="Pickup Time" value={formatDetailDateTime(allOrders["pickup-time"])} />}
          </div>
          {allOrders.notes && (
            <div className="notes-section">
              <h3>Notes</h3>
              <p>{allOrders.notes}</p>
            </div>
          )}
          {orderMetadata && (
            <div className="scan-metadata-section">
              <h3>Scan Delivery</h3>
              <div className="detail-grid">
                <div className="detail-item"><div className="detail-label">Order ID</div><div className="detail-value"><code>{orderMetadata.order_id}</code></div></div>
                <DI label="Delivery Type" value={sd.type} />
                <DI label="Total Folders" value={orderMetadata.total_folders || 0} />
                <DI label="Total Files" value={orderMetadata.total_files || 0} />
              </div>
              {orderMetadata.metadata?.length > 0 && (
                <div className="roll-breakdown" style={{ marginTop: 12 }}>
                  <table>
                    <thead><tr><th>FOLDER</th><th>FILES</th></tr></thead>
                    <tbody>
                      {orderMetadata.metadata.map((m: any, i: number) => (
                        <tr key={i}><td>{m.folder_name}</td><td>{m.file_count || 0}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {filmDrop && (
        <div className="detail-card">
          <h2>Film Drop Submission</h2>
          <div className="detail-grid">
            <DI label="Submitted" value={formatDetailDateTime(filmDrop.timestamp)} />
            <DI label="Customer" value={filmDrop["full-name"] || "N/A"} />
            <div className="detail-item">
              <div className="detail-label">Email</div>
              <div className="detail-value">{filmDrop.email ? <a href={`mailto:${filmDrop.email}`}>{filmDrop.email}</a> : "N/A"}</div>
            </div>
            <div className="detail-item"><div className="detail-label">Total Rolls</div><div className="detail-value"><strong>{totalRolls}</strong></div></div>
          </div>

          {rollTypes.length > 0 && (
            <div className="roll-breakdown">
              <h3>Roll Breakdown:</h3>
              <table>
                <thead><tr><th>TYPE</th><th>QUANTITY</th></tr></thead>
                <tbody>
                  {rollTypes.map((r, i) => (
                    <tr key={i}><td>{r.type}</td><td>{r.qty}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="detail-grid" style={{ marginTop: 16 }}>
            <DI label="Negative Return" value={filmDrop["negative-return"] || "N/A"} />
            <DI label="Negative Pickup" value={filmDrop["negative-pickup"] || "N/A"} />
            <DI label="Scan Resolution" value={filmDrop["scan-resolution"] || "N/A"} />
            <DI label="File Format" value={filmDrop["file-format"] || "N/A"} />
          </div>

          <div className="detail-grid" style={{ marginTop: 16 }}>
            <div className="detail-item">
              <div className="detail-label">Services Requested</div>
              <div className="detail-value">
                {filmDrop["scan-gallery"] && <>✓ Scan Gallery<br /></>}
                {filmDrop["contact-sheet"] && <>✓ Contact Sheet<br /></>}
                {filmDrop.prints && <>✓ Prints<br /></>}
                {!filmDrop["scan-gallery"] && !filmDrop["contact-sheet"] && !filmDrop.prints && "None"}
              </div>
            </div>
            <DI label="Club Member" value={filmDrop["club-member"] ? "Yes" : "No"} />
          </div>

          {(filmDrop.street || filmDrop["mailing-address"]) && (
            <div className="detail-grid" style={{ marginTop: 16 }}>
              <div className="detail-item" style={{ gridColumn: "span 2" }}>
                <div className="detail-label">Mailing Address</div>
                <div className="detail-value">
                  {filmDrop.street
                    ? [filmDrop.street, filmDrop.street2, [filmDrop.city, filmDrop.state, filmDrop.zip].filter(Boolean).join(", ")]
                        .filter(Boolean)
                        .map((line: string, i: number) => <div key={i}>{line}</div>)
                    : String(filmDrop["mailing-address"] || "")
                        .split("\n")
                        .map((line, i) => <div key={i}>{line}</div>)}
                </div>
              </div>
            </div>
          )}

          {(docs.length > 0 || invoiceId.startsWith("in_") || showBuyLabelBtn) && (
            <div className="documents-section">
              <h3>Forms &amp; Documents:</h3>
              <div className="doc-buttons">
                {docs.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer" className="doc-button">
                    {d.icon} {d.label}
                  </a>
                ))}
                {invoiceId.startsWith("in_") && (
                  <a href={`https://dashboard.stripe.com/invoices/${invoiceId}`} target="_blank" rel="noreferrer" className="doc-button stripe-button">
                    💳 View in Stripe
                  </a>
                )}
                {showBuyLabelBtn && (
                  <a href={buyLabelUrl} target="_blank" rel="noreferrer" className="doc-button buy-label-button">
                    📦 Buy New Label
                  </a>
                )}
              </div>
            </div>
          )}

          {filmDrop.notes && (
            <div className="notes-section">
              <h3>Customer Notes</h3>
              <p>{filmDrop.notes}</p>
            </div>
          )}
        </div>
      )}

      {!filmDrop && allOrders && (
        <div className="detail-card">
          <h2>Film Drop Submission</h2>
          <div className="loading-message" style={{ padding: 20 }}>
            This is a legacy order created before the film drop form system. No submission details available.
          </div>
        </div>
      )}

      {filmDrop && !allOrders && (
        <div className="detail-card">
          <h2>Order Information</h2>
          <div className="loading-message" style={{ padding: 20, background: "#fff3cd", borderRadius: 6 }}>
            <i className="ph ph-hourglass" /> This order is still being processed. Delivery information will appear here once the order is complete.
          </div>
        </div>
      )}
    </>
  );
}

function DI({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value}</div>
    </div>
  );
}
